import { FormEvent, useState } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  Lock,
  MapPin,
  Shield,
  Star,
  TrendUp,
  Trophy,
  Users,
} from '@phosphor-icons/react'
import Logo from '@/components/Logo'
import { SEO } from '@/components/SEO'

interface HomePageProps {
  onLogin: () => void
  onSearch: (zip: string, service: string) => void
  onLockSite?: () => void
  onNavigate: (page: 'services' | 'how-it-works' | 'become-contractor' | 'about' | 'contact' | 'terms' | 'privacy' | 'cookies' | 'cost-calculator' | 'faq' | 'browse-states') => void
}

const serviceOptions = [
  { value: 'all', label: 'All paving services' },
  { value: 'sealcoating', label: 'Sealcoating' },
  { value: 'paving', label: 'Asphalt paving' },
  { value: 'asphalt-repair', label: 'Asphalt repair' },
  { value: 'crack-filling', label: 'Crack filling' },
  { value: 'striping', label: 'Striping & marking' },
]

const heroStats = [
  { icon: Users, value: '470+', label: 'Listed Contractors' },
  { icon: Star, value: '4.5+', label: 'Min Rating Threshold' },
  { icon: Clock, value: '24hr', label: 'Response Goal' },
  { icon: CheckCircle, value: '50', label: 'States Covered' },
]

const services = [
  { name: 'Sealcoating', icon: Shield, description: 'Connect with trusted sealcoating professionals in your area', color: 'from-sky-500 to-blue-600', value: 'sealcoating' },
  { name: 'Crack Filling', icon: CheckCircle, description: 'Find experts for professional crack sealing and repair', color: 'from-emerald-500 to-green-600', value: 'crack-filling' },
  { name: 'Striping & Line Marking', icon: Star, description: 'Connect with professional striping and marking specialists', color: 'from-amber-400 to-orange-500', value: 'striping' },
  { name: 'Asphalt Repair', icon: Clock, description: 'Find skilled professionals for asphalt repair and restoration', color: 'from-rose-500 to-red-600', value: 'asphalt-repair' },
  { name: 'Resurfacing', icon: TrendUp, description: 'Connect with resurfacing and overlay specialists', color: 'from-violet-500 to-purple-600', value: 'paving' },
  { name: 'Cost Estimator', icon: Calendar, description: 'Get general cost estimates to help plan your project budget', color: 'from-teal-500 to-emerald-600', value: 'all' },
]

const contractors = [
  { name: 'Precision Sealcoat & Paving', city: 'Dallas, TX', rating: 4.9, reviews: 127, rank: '#1' },
  { name: 'All-Star Asphalt Co.', city: 'Orlando, FL', rating: 4.8, reviews: 98, rank: '#2' },
  { name: 'Pacific Paving Pros', city: 'Portland, OR', rating: 4.9, reviews: 84, rank: '#3' },
  { name: 'Anchor Paving Co.', city: 'Anchorage, AK', rating: 4.7, reviews: 63, rank: '#4' },
]

const steps = [
  { number: '01', title: 'Tell Us Your Needs', description: 'Enter your ZIP code and describe your project. It takes less than two minutes.', icon: MapPin, color: 'from-sky-500 to-blue-600' },
  { number: '02', title: 'Get Matched', description: 'We surface verified contractors in your area that fit the kind of work you need.', icon: Users, color: 'from-emerald-500 to-green-600' },
  { number: '03', title: 'Compare and Choose', description: 'Review profiles, trust signals, and quote paths before choosing your contractor.', icon: Star, color: 'from-amber-400 to-orange-500' },
  { number: '04', title: 'Get It Done', description: 'Connect directly, move your project forward, and rate the finished work.', icon: CheckCircle, color: 'from-violet-500 to-purple-600' },
]

export function HomePage({ onLogin, onSearch, onLockSite, onNavigate }: HomePageProps) {
  const [zipCode, setZipCode] = useState('')
  const [service, setService] = useState('all')
  const [zipError, setZipError] = useState('')

  const handleSearch = (event?: FormEvent) => {
    event?.preventDefault()
    if (!/^\d{5}$/.test(zipCode)) {
      setZipError('Enter a valid 5-digit ZIP code.')
      return
    }

    setZipError('')
    onSearch(zipCode, service)
  }

  return (
    <div className="site-surface relative min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,rgba(255,140,0,0.14),transparent_32%),linear-gradient(180deg,#f7f9ff_0%,#eef3ff_34%,#f8f4ef_68%,#ffffff_100%)] text-slate-950">
      <SEO
        title="SealcoatPaving.com | Find Trusted Sealcoating & Paving Contractors"
        description="Search verified sealcoating and paving contractors by ZIP code. Compare ratings, browse services, and request free quotes from top-rated pros near you."
        path="/"
      />

      <section className="relative overflow-hidden">
        <div className="hero-material absolute inset-0" aria-hidden="true" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.95),transparent_26%),radial-gradient(circle_at_85%_18%,rgba(255,201,138,0.42),transparent_20%),radial-gradient(circle_at_50%_70%,rgba(173,198,255,0.18),transparent_28%)]" />
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute h-3 w-3 rounded-full bg-white/70 shadow-[0_0_30px_rgba(152,183,255,0.6)]"
              animate={{ x: [0, 80, 0], y: [0, -70, 0], opacity: [0.25, 0.75, 0.25] }}
              transition={{ duration: 8 + i * 2, repeat: Infinity, delay: i * 0.8 }}
              style={{ left: `${20 + i * 15}%`, top: `${26 + i * 11}%` }}
            />
          ))}
        </div>

        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-24">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <motion.div
              className="space-y-8 lg:col-span-7"
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-3 rounded-full border border-white/60 bg-white/60 px-4 py-2 text-[0.7rem] font-semibold uppercase tracking-[0.24em] text-slate-700 shadow-[0_12px_40px_rgba(148,163,184,0.16)] backdrop-blur-xl">
                <Lock size={12} className="text-orange-500" weight="bold" />
                Verified Contractor Network
              </div>

              <div className="space-y-5">
                <h1 className="max-w-4xl text-[clamp(3.2rem,8vw,6.8rem)] font-black uppercase leading-[0.88] tracking-[-0.04em] text-slate-950">
                  Find
                  <span className="block bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_45%,#171717_100%)] bg-clip-text text-transparent">
                    Trusted Pros
                  </span>
                  <span className="block text-slate-800">Near You</span>
                </h1>
                <p className="max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl">
                  We connect property owners with verified sealcoating and paving contractors through a cleaner, faster, premium search experience.
                </p>
              </div>

              <form
                onSubmit={handleSearch}
                className="rounded-[2rem] border border-white/60 bg-white/55 p-4 shadow-[0_24px_90px_rgba(15,23,42,0.12)] backdrop-blur-2xl"
              >
                <div className="grid gap-3 lg:grid-cols-[1.1fr_1.1fr_auto]">
                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Service needed</span>
                    <select
                      value={service}
                      onChange={(event) => setService(event.target.value)}
                      className="h-16 w-full rounded-2xl border border-white/70 bg-white/85 px-5 text-base font-semibold text-slate-900 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                    >
                      {serviceOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">ZIP code</span>
                    <input
                      inputMode="numeric"
                      autoComplete="postal-code"
                      value={zipCode}
                      onChange={(event) => {
                        setZipCode(event.target.value.replace(/\D/g, '').slice(0, 5))
                        setZipError('')
                      }}
                      placeholder="Enter ZIP code"
                      className="h-16 w-full rounded-2xl border border-white/70 bg-white/85 px-5 text-lg font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                    />
                  </label>
                  <button
                    type="submit"
                    className="mt-6 inline-flex h-16 items-center justify-center gap-3 rounded-2xl bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_100%)] px-7 text-sm font-bold uppercase tracking-[0.18em] text-white shadow-[0_20px_45px_rgba(255,120,35,0.35)] transition hover:scale-[1.01] hover:shadow-[0_26px_55px_rgba(255,120,35,0.42)] lg:mt-7"
                  >
                    Find Contractors <ArrowRight size={18} weight="bold" />
                  </button>
                </div>
                {zipError ? <p role="alert" className="px-1 pt-3 text-sm font-semibold text-rose-600">{zipError}</p> : null}
                <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-slate-600">
                  <span className="flex items-center gap-2"><CheckCircle weight="fill" className="text-emerald-500" /> Free referral</span>
                  <span className="flex items-center gap-2"><CheckCircle weight="fill" className="text-emerald-500" /> Verified network</span>
                  <button
                    type="button"
                    onClick={() => onNavigate('cost-calculator')}
                    className="font-semibold text-orange-600 underline decoration-orange-300 underline-offset-4"
                  >
                    Estimate project cost
                  </button>
                </div>
              </form>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              <div className="relative mx-auto max-w-xl">
                <div className="absolute -left-8 top-10 h-36 w-36 rounded-full bg-orange-300/35 blur-3xl" />
                <div className="absolute -bottom-4 right-4 h-32 w-32 rounded-full bg-sky-200/40 blur-3xl" />
                <div className="relative overflow-hidden rounded-[2.25rem] border border-white/60 bg-white/30 shadow-[0_30px_120px_rgba(15,23,42,0.18)] backdrop-blur-2xl">
                  <img
                    src="/sealcoatpaving_enterprise_brand_system/01_masters/B214451F-12EF-4356-9F67-797F2682D2B3.jpeg"
                    alt="SealcoatPaving branded hero"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-[linear-gradient(180deg,transparent_0%,rgba(7,10,18,0.88)_100%)] p-6 text-white">
                    <div className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70">SealcoatPaving.com</div>
                    <div className="mt-2 text-2xl font-bold leading-tight">Professional asphalt sealcoating and paving</div>
                    <div className="mt-4 flex items-center gap-4 text-sm text-white/80">
                      <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-md">
                        <Clock size={14} /> 24hr Response Goal
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {heroStats.map((stat, index) => (
              <motion.div
                key={stat.label}
                className="rounded-[1.75rem] border border-white/60 bg-white/55 p-6 text-center shadow-[0_20px_60px_rgba(148,163,184,0.12)] backdrop-blur-xl"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(255,140,0,0.18),rgba(255,255,255,0.8))]">
                  <stat.icon size={28} className="text-orange-500" />
                </div>
                <div className="text-4xl font-black text-slate-950">{stat.value}</div>
                <div className="mt-1 font-medium text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24">
        <div className="absolute inset-0 -skew-y-3 scale-110 bg-[linear-gradient(135deg,rgba(255,255,255,0.45),rgba(224,233,255,0.45))]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-16 space-y-4 text-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-5xl font-black text-slate-950">Our <span className="bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_100%)] bg-clip-text text-transparent">Services</span></h2>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-slate-600">
              Find trusted contractors for comprehensive asphalt and paving solutions through our verified referral network.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((serviceCard, index) => (
              <motion.button
                key={serviceCard.name}
                onClick={() => serviceCard.name === 'Cost Estimator' ? onNavigate('cost-calculator') : onSearch(zipCode || '00000', serviceCard.value)}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                className="group relative w-full overflow-hidden rounded-[2rem] border border-white/60 bg-white/60 p-8 text-left shadow-[0_20px_70px_rgba(148,163,184,0.14)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_90px_rgba(148,163,184,0.18)]"
              >
                <div className={`mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${serviceCard.color} shadow-lg transition-transform duration-300 group-hover:scale-110`}>
                  <serviceCard.icon size={30} className="text-white" />
                </div>
                <h3 className="mb-3 text-xl font-bold text-slate-950 transition-colors group-hover:text-orange-600">{serviceCard.name}</h3>
                <p className="leading-relaxed text-slate-600">{serviceCard.description}</p>
                <div className={`mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r ${serviceCard.color} px-4 py-2 text-sm font-semibold text-white shadow`}>
                  Free Referral
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[linear-gradient(180deg,rgba(255,255,255,0.25)_0%,rgba(237,242,255,0.72)_100%)] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 space-y-4 text-center">
            <div className="mx-auto mb-4 inline-flex items-center justify-center rounded-full bg-amber-100/90 p-3 shadow-lg">
              <Trophy size={32} className="text-amber-600" weight="duotone" />
            </div>
            <h2 className="text-3xl font-black tracking-tight text-slate-950">Best of USA — Top Paving Pros</h2>
            <p className="mx-auto max-w-2xl text-slate-600">
              We rigorously cross-reference public data sources to curate the most reliable, highly-rated Diamond-Level paving and sealcoating contractors across America.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {contractors.map((contractor, index) => (
              <motion.div
                key={contractor.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="group relative h-full overflow-hidden rounded-[1.5rem] border border-white/60 bg-white/65 p-5 shadow-[0_20px_70px_rgba(148,163,184,0.14)] backdrop-blur-xl transition-all hover:border-amber-300 hover:shadow-[0_24px_80px_rgba(148,163,184,0.2)]">
                  <div className="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-gradient-to-bl from-amber-200/90 to-transparent opacity-80 transition-transform group-hover:scale-150" />
                  <div className="flex h-full flex-col justify-between gap-4">
                    <div className="space-y-2">
                      <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                        Rank {contractor.rank}
                      </span>
                      <h3 className="text-lg font-bold leading-tight text-slate-950 transition-colors group-hover:text-amber-700">{contractor.name}</h3>
                    </div>
                    <div className="mt-auto space-y-2">
                      <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <MapPin size={16} />
                        {contractor.city}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center text-amber-500">
                          <Star size={16} weight="fill" />
                          <span className="ml-1 font-bold">{contractor.rating}</span>
                        </div>
                        <span className="text-xs text-slate-500">({contractor.reviews} verified reviews)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.45),transparent_55%)]" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            className="mb-16 space-y-4 text-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-5xl font-black text-slate-950">How It <span className="bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_100%)] bg-clip-text text-transparent">Works</span></h2>
            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-slate-600">
              Get connected with trusted contractors in four simple steps.
            </p>
          </motion.div>

          <div className="mx-auto grid max-w-3xl gap-10">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="relative"
                initial={{ opacity: 0, y: 80 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
              >
                <div className="relative mx-auto max-w-lg rounded-[2rem] border border-white/60 bg-white/60 p-8 shadow-[0_22px_80px_rgba(148,163,184,0.15)] backdrop-blur-xl">
                  <div className="absolute -top-6 left-8 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-orange-400 shadow-lg">
                    <span className="text-lg font-bold text-white">{step.number}</span>
                  </div>
                  <div className={`mx-auto mb-6 mt-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br ${step.color}`}>
                    <step.icon size={32} className="text-white" />
                  </div>
                  <div className="space-y-3 text-center">
                    <h3 className="text-xl font-bold text-slate-950">{step.title}</h3>
                    <p className="leading-relaxed text-slate-600">{step.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden py-24">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,140,0,0.08),rgba(59,130,246,0.08))]" />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="space-y-6">
              <span className="inline-block rounded-full border border-orange-200 bg-white/70 px-4 py-2 text-sm font-semibold text-orange-600 shadow-sm backdrop-blur-xl">
                For Contractors
              </span>
              <h2 className="text-5xl font-black leading-tight text-slate-950">
                Grow Your
                <span className="block bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_100%)] bg-clip-text text-transparent">Paving Business</span>
              </h2>
              <p className="text-xl leading-relaxed text-slate-600">
                Join our exclusive network of verified contractors and get premium leads from property owners in your service area.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Users, value: '500+', label: 'Active Contractors' },
                { icon: TrendUp, value: '30%', label: 'Avg Revenue Increase' },
                { icon: Star, value: '4.9', label: 'Customer Rating' },
                { icon: Clock, value: '24hr', label: 'Lead Response' },
              ].map((item) => (
                <div key={item.label} className="rounded-[1.5rem] border border-white/50 bg-white/55 p-5 text-center shadow-[0_18px_60px_rgba(148,163,184,0.12)] backdrop-blur-xl">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-orange-100/90">
                    <item.icon size={24} className="text-orange-500" />
                  </div>
                  <div className="text-2xl font-black text-orange-600">{item.value}</div>
                  <div className="text-sm text-slate-600">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-4 sm:flex-row">
              <button
                onClick={() => onNavigate('become-contractor')}
                className="rounded-2xl bg-[linear-gradient(135deg,#ff8c00_0%,#ff5a36_100%)] px-8 py-4 text-lg font-semibold text-white shadow-[0_20px_45px_rgba(255,120,35,0.35)]"
              >
                Join Our Network
              </button>
              <button
                onClick={() => onNavigate('become-contractor')}
                className="rounded-2xl border border-white/70 bg-white/60 px-8 py-4 text-lg font-semibold text-slate-800 backdrop-blur-xl"
              >
                Learn More
              </button>
            </div>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="mx-auto max-w-md overflow-hidden rounded-[2rem] border border-white/60 bg-white/30 shadow-[0_26px_100px_rgba(148,163,184,0.18)] backdrop-blur-xl">
              <img
                src="/sealcoatpaving_enterprise_brand_system/01_masters/85904B45-9348-4117-A03F-62BF51DFB059.jpeg"
                alt="SealcoatPaving brand impact artwork"
                className="block h-auto w-full"
                loading="lazy"
              />
              <div className="bg-[linear-gradient(180deg,rgba(17,24,39,0.92)_0%,rgba(17,24,39,0.98)_100%)] px-6 py-5 text-white">
                <div className="text-3xl font-black">470+</div>
                <div className="text-xs uppercase tracking-[0.28em] text-white/70">Trusted Contractors</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-white/40 bg-white/45 py-16 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-4">
              <Logo size={68} />
              <p className="leading-relaxed text-slate-600">
                Connecting property owners with trusted paving professionals through a premium referral network.
              </p>
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                <span className="text-sm text-slate-500">Always available</span>
              </div>
            </div>
            {[
              { title: 'Services', links: [{ label: 'Sealcoating', page: 'services' }, { label: 'Crack Filling', page: 'services' }, { label: 'Asphalt Repair', page: 'services' }, { label: 'Striping', page: 'services' }] },
              { title: 'Company', links: [{ label: 'About Us', page: 'about' }, { label: 'How It Works', page: 'how-it-works' }, { label: 'Browse by State', page: 'browse-states' }, { label: 'Become a Contractor', page: 'become-contractor' }, { label: 'Contact', page: 'contact' }, { label: 'FAQ', page: 'faq' }] },
              { title: 'Legal', links: [{ label: 'Terms of Service', page: 'terms' }, { label: 'Privacy Policy', page: 'privacy' }, { label: 'Cookie Policy', page: 'cookies' }] },
            ].map((section) => (
              <div key={section.title} className="space-y-4">
                <h4 className="text-lg font-semibold text-slate-950">{section.title}</h4>
                <div className="space-y-2">
                  {section.links.map((link) => (
                    <button
                      key={link.label}
                      onClick={() => onNavigate(link.page as HomePageProps['onNavigate'] extends (page: infer P) => void ? P : never)}
                      className="block text-left text-slate-600 transition hover:text-orange-600"
                    >
                      {link.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/50 pt-8 text-center text-slate-500">
            <p>Contractor data sourced from OpenStreetMap contributors under ODbL.</p>
            <p className="mt-1">© 2026 SealcoatPaving.com. All rights reserved.</p>
            {onLockSite ? (
              <button
                type="button"
                onClick={onLockSite}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-300/80 bg-white/55 px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-slate-600 transition hover:border-orange-300 hover:text-orange-600"
              >
                <Lock size={13} weight="bold" /> Lock Site
              </button>
            ) : null}
          </div>
        </div>
      </footer>
    </div>
  )
}

export default HomePage
